import { useEffect, useRef } from 'react'

/** Меньше этого — панель браузера, а не клавиатура. */
const MIN_KEYBOARD_PX = 80
/** Воздух между курсором и клавиатурой. */
const CARET_GAP_PX = 16

const caretBottom = (el: HTMLElement): number => {
  const selection = window.getSelection()
  const range = el.isContentEditable && selection?.rangeCount ? selection.getRangeAt(0) : null
  const rect = range?.getClientRects()[0] ?? el.getBoundingClientRect()
  return rect.bottom
}

const focusedElement = (): HTMLElement | null => {
  const el = document.activeElement
  return el instanceof HTMLElement ? el : null
}

/**
 * Клавиатура телефона сжимает только видимую область, раскладка о ней не знает. Хук отдаёт её
 * высоту в `--keyboard-inset` (по ней поднимается шторка), а холст сдвигает вверх настолько, чтобы
 * курсор остался виден. Клавиатура закрыта — всё на своих местах.
 */
export const useKeyboardAvoid = () => {
  const canvasRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    const root = document.documentElement
    let shift = 0

    const update = () => {
      const canvas = canvasRef.current
      // Экранной клавиатуры без грубого указателя нет: щипок на тачпаде тоже сжимает visualViewport.
      const coarse = window.matchMedia('(pointer: coarse)').matches
      const raw = coarse ? window.innerHeight - viewport.height - viewport.offsetTop : 0
      const inset = raw >= MIN_KEYBOARD_PX ? raw : 0
      root.style.setProperty('--keyboard-inset', `${inset}px`)
      if (!canvas) return
      const focused = focusedElement()
      let next = 0
      if (inset > 0 && focused && canvas.contains(focused)) {
        const visibleBottom = viewport.offsetTop + viewport.height
        // Замер уже со сдвигом: прибавляем его, чтобы получить положение без сдвига.
        next = Math.max(0, caretBottom(focused) + shift + CARET_GAP_PX - visibleBottom)
      }
      if (next !== shift) {
        shift = next
        canvas.style.transform = shift > 0 ? `translateY(${-shift}px)` : ''
      }
    }

    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    document.addEventListener('selectionchange', update)
    document.addEventListener('focusin', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      document.removeEventListener('selectionchange', update)
      document.removeEventListener('focusin', update)
      root.style.removeProperty('--keyboard-inset')
    }
  }, [])

  return canvasRef
}
